import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Leaf } from "lucide-react";
import api from "@/lib/api";

// Warm, lighter orange (#F6B27A) is used for hover / focus / active nav states

export default function Header({ settings }) {
  const [nav, setNav] = useState([]);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    api.get("/public/navigation").then((r) => setNav(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const phoneTel = settings?.phone_tel || "+12894421265";
  const phone = settings?.phone || "+1 (289) 442-1265";
  const cta = settings?.header_cta || { label: "Call Us", url: `tel:${phoneTel}` };
  const ctaUrl = cta.url || `tel:${phoneTel}`;
  const ctaIsTel = ctaUrl.startsWith("tel:");
  const ctaExternal = /^(https?:|tel:|mailto:)/.test(ctaUrl);

  const whiteHeader = (settings?.header_style || "transparent") === "white";
  const hasHero = loc.pathname === "/";               // only the homepage has a dark hero behind the header
  const solid = whiteHeader || scrolled || !hasHero;  // solid bg + dark text everywhere except transparent-over-hero
  const shadow = solid ? "" : "[text-shadow:_0_1px_3px_rgb(0_0_0_/_0.55)]";

  const headerBg = solid
    ? (whiteHeader
        ? "bg-white shadow-sm border-b border-stone-200"
        : "backdrop-blur-md bg-stone-50/95 shadow-sm border-b border-stone-200/80")
    : "bg-gradient-to-b from-black/50 to-transparent";

  const navLinkClass = (active) => {
    const base = "text-sm font-semibold transition-colors rounded-sm px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:ring-[#F6B27A]";
    if (solid) {
      return `${base} ${active ? "text-[#C85A32]" : "text-emerald-900 hover:text-[#C85A32] focus-visible:text-[#C85A32]"}`;
    }
    return `${base} ${shadow} ${active ? "text-[#F6B27A]" : "text-white hover:text-[#F6B27A] focus-visible:text-[#F6B27A]"}`;
  };

  const CtaButton = (
    <span className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] text-white text-sm font-semibold px-4 py-2 hover:bg-[#b24d28] transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B27A] focus-visible:ring-offset-2">
      {ctaIsTel && <Phone size={15} />} {cta.label || "Call Us"}
    </span>
  );

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${headerBg}`} data-testid="site-header">
      {/* Announcement strip (preserved) */}
      <div className="hidden md:flex justify-end items-center gap-6 px-6 lg:px-12 py-1.5 bg-emerald-950 text-amber-50 text-xs font-mono">
        <span>{settings?.appointment_note || "All visits and consultations by appointment only"}</span>
        <a href={`tel:${phoneTel}`} className="flex items-center gap-1.5 hover:text-amber-200 focus-visible:outline-none focus-visible:underline" data-testid="topbar-phone">
          <Phone size={12} /> {phone}
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B27A] rounded-lg" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-full bg-emerald-800 text-amber-50 flex items-center justify-center flex-shrink-0">
            <Leaf size={18} />
          </span>
          <span className={`font-serif text-lg leading-tight ${solid ? "text-emerald-950" : `text-white ${shadow}`}`}>
            Healthy Roots<span className={`block text-[10px] font-sans tracking-widest uppercase ${solid ? "text-emerald-700" : "text-amber-100/90"}`}>Holistic Practitioners</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {nav.map((item, i) => {
            const active = loc.pathname === item.url;
            return (
              <Link key={`${item.url}-${item.label}-${i}`} to={item.url}
                aria-current={active ? "page" : undefined}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={navLinkClass(active)}>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {ctaExternal
            ? <a href={ctaUrl} data-testid="header-call-to-order" className="hidden sm:inline-flex">{CtaButton}</a>
            : <Link to={ctaUrl} data-testid="header-call-to-order" className="hidden sm:inline-flex">{CtaButton}</Link>}
          <button
            className={`lg:hidden rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B27A] ${solid ? "text-emerald-950" : `text-white ${shadow}`}`}
            onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-stone-50 border-t border-stone-200 px-4 py-4 space-y-1 shadow-lg" data-testid="mobile-menu">
          {nav.map((item, i) => (
            <Link key={`m-${item.url}-${item.label}-${i}`} to={item.url}
              aria-current={loc.pathname === item.url ? "page" : undefined}
              className={`block px-3 py-2.5 rounded-lg font-semibold hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B27A] ${loc.pathname === item.url ? "text-[#C85A32]" : "text-emerald-950"}`}>
              {item.label}
            </Link>
          ))}
          {ctaExternal
            ? <a href={ctaUrl} className="block px-3 py-2.5 rounded-lg text-[#C85A32] font-semibold">{cta.label || "Call to Order"}{ctaIsTel ? ` ${phone}` : ""}</a>
            : <Link to={ctaUrl} className="block px-3 py-2.5 rounded-lg text-[#C85A32] font-semibold">{cta.label || "Call to Order"}</Link>}
        </div>
      )}
    </header>
  );
}
